// @flow
import React, { Fragment } from 'react';

import Link from 'app/common/components/Link';
import help from 'app/developer-v3/components/PartnerSupportForm/components/blocks/Help';
import radio from 'app/developer-v3/components/PartnerSupportForm/components/blocks/Radio';
import { createDefaultContact } from 'app/developer-v3/components/PartnerSupportForm/content/nodes/common/defaultContact';
import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import { BASE_DOC_URL_VB } from 'app/developer-v3/constants';
import {
  getDefaultFields,
  getUserFields,
  messageField,
} from 'app/developer-v3/components/PartnerSupportForm/content/nodes/common';

export const createGettingStarted = (
  isLoggedIn: boolean,
  existingAppOrNewApp: string
) => ({
  value: `${existingAppOrNewApp}-started`,
  label: 'Getting started',
  zendeskCustomFieldValue: `pcf-${existingAppOrNewApp}-building-started`,
  child: help({
    docs: {
      title: 'Getting Started building Your App',
      body: () => (
        <Fragment>
          <p>
            <Link href={`${ZAPIER_APP_BASE_URL}/app/dashboard`} target="_blank">
              Zapier
            </Link>{' '}
            is an app automation platform where over 2 million people connect
            apps into customized workflows—what we call Zaps. A Zapier
            integration can connect your app’s API to the Zapier platform and
            let it integrate with 6,000+ other popular apps to watch for new or
            updated data, find existing data, or create and update data.
          </p>
        </Fragment>
      ),
      links: [
        {
          href: `${BASE_DOC_URL_VB}/quickstart/how-zapier-works`,
          title: 'Quick Start Guide - Dev Platform',
        },
      ],
    },
    contact: [
      ...getUserFields(isLoggedIn),
      {
        ...messageField,
        details: `Please let us know what questions you have about getting started.`,
      },
    ],
  }),
});

export const createAuthentication = (
  isLoggedIn: boolean,
  existingAppOrNewApp: string
) => ({
  value: `${existingAppOrNewApp}-authentication`,
  label: 'Authentication',
  zendeskCustomFieldValue: `pcf-${existingAppOrNewApp}-building-authentication`,
  child: help({
    docs: {
      title: 'Authentication in your App',
      body: () => (
        <Fragment>
          <p>
            The first thing to set up is authentication. Your integration
            defines how Zapier’s platform authenticates with the API and what
            data needs to be collected from users to allow access to their
            accounts. Zapier supports most popular authentication schemes,
            including basic auth with username and password, API key auth,
            digest, session, and OAuth v2.
          </p>
        </Fragment>
      ),
      links: [
        {
          href: `${BASE_DOC_URL_VB}/build/auth`,
          title: 'Authentication Section - Quick Start Guide',
        },
      ],
    },
    contact: [
      ...getUserFields(isLoggedIn),
      {
        ...messageField,
        details: `Please let us know what questions you have about Authentication.`,
      },
    ],
  }),
});

export const createTriggersAndActions = (
  isLoggedIn: boolean,
  newOrExistingApp: string
) => ({
  value: 'triggers',
  label: 'Building triggers, actions, or searches',
  zendeskCustomFieldValue: `pcf-${newOrExistingApp}-building-triggers-actions-searches`,
  child: radio({
    id: 'triggeractionsearch',
    label: "Let's get you some help",
    zendeskCustomFieldId:
      newOrExistingApp === 'new' ? 360044603691 : 360044584772,
    items: [
      {
        value: `${newOrExistingApp}-started-triggers`,
        label: 'Getting started',
        zendeskCustomFieldValue: `pcf-${newOrExistingApp}-building-triggers-started`,
        child: help({
          docs: {
            title: 'Getting started with triggers, actions, or searches',
            body: () => (
              <Fragment>
                <p>
                  Zapier triggers get new data from your API, parse individual
                  data fields, and let users include that data in subsequent Zap
                  action steps. Triggers can run every time something new is
                  added to an API endpoint or pushed to Zapier via a Webhook, or
                  they can use filters to watch for specific items.
                </p>
                <p>
                  Zapier actions push or put new data into your API from input
                  fields that users set in their Zaps. Zapier includes search
                  actions to find data in an app, and create actions to add new
                  items via API calls.
                </p>
                <p>
                  Creating both for your integration allows you to fully unleash
                  workflow opportunities with any app on Zapier
                </p>
              </Fragment>
            ),
            links: [
              {
                href: `${BASE_DOC_URL_VB}/build/trigger`,
                title: 'Quick Start - Building Triggers',
              },
              {
                href: `${BASE_DOC_URL_VB}/build/action`,
                title: 'Quick Start - Building Actions',
              },
            ],
          },
          contact: [
            ...getUserFields(isLoggedIn),
            {
              ...messageField,
              details: `Please let us know what questions you have about building Triggers/Actions/Searches.`,
            },
          ],
        }),
      },
      {
        value: `${newOrExistingApp}-line-items`,
        label: 'Working with line items/arrays',
        zendeskCustomFieldValue: `pcf-${newOrExistingApp}-building-triggers-lineitems`,
        child: help({
          docs: {
            title: 'Working with line items/arrays',
            body: () => (
              <Fragment>
                <p>
                  Line Items are a useful way to add as much data to an app as
                  users need, especially in invoices, orders, and other
                  accounting work. Line items can be received in a trigger
                  event, or sent in an action event.
                </p>
              </Fragment>
            ),
            links: [
              {
                href: `${BASE_DOC_URL_VB}/build/line-items`,
                title: 'Add line item group field to actions',
              },
              {
                href: `${BASE_DOC_URL_VB}/build/response-types#return-your-data-as-line-items-for-search-or-create-actions`,
                title:
                  'Return your data as line items for search or create actions',
              },
            ],
          },
          contact: [
            ...getUserFields(isLoggedIn),
            {
              ...messageField,
              details: `Please let us know what questions you have about working with line items/arrays.`,
            },
          ],
        }),
      },
      {
        value: `${newOrExistingApp}-dehydration`,
        label: 'Dehydration/working with files',
        zendeskCustomFieldValue: `pcf-${newOrExistingApp}-building-triggers-dehydration`,
        child: help({
          docs: {
            title: 'Dehydration/working with files',
            body: () => (
              <Fragment>
                <p>
                  Accessing files in a Zap is made easy with hydration and its
                  counterpart, dehydration, both available in the{' '}
                  <Link
                    href={`${BASE_DOC_URL_VB}/reference/cli-docs#file-dehydration`}
                    target="_blank"
                  >
                    Platform CLI
                  </Link>
                  . Your app’s integration gets 30 seconds to run its API calls
                  and any additional code, and hydration helps to save bandwidth
                  and time to make files accessible inside a user’s Zap.
                </p>
              </Fragment>
            ),
            links: [
              {
                href: `${BASE_DOC_URL_VB}/build/hydration-cli`,
                title: 'CLI Tutorials - Hydration',
              },
            ],
          },
          contact: [
            ...getUserFields(isLoggedIn),
            {
              ...messageField,
              details: `Please let us know what questions you have about dehydration/working with files.`,
            },
          ],
        }),
      },
      {
        // Only rendered if no app selected or selected app is Visual Builder
        value: `${newOrExistingApp}-subscription-webhook`,
        label: 'Working with subscription webhook triggers',
        zendeskCustomFieldValue: `pcf-${newOrExistingApp}-building-triggers-subscription-webhook`,
        shouldRender: context =>
          !context.selectedApp ||
          (context.selectedApp && !context.selectedApp.isCLIIntegration),
        child: help({
          docs: {
            title: 'Working with subscription webhook triggers',
            body: () => (
              <Fragment>
                <p>
                  In subscription webhook triggers, also known as REST hook
                  triggers, a webhook subscription is created between Zapier and
                  your application. When relevant events occur in your
                  application, post to the target URL provided by Zapier to
                  trigger a user’s corresponding Zap.
                </p>
                <p>
                  Unlike polling triggers where we make multiple polling
                  requests at intervals to fetch the items and trigger on the
                  new items, here, we do not make polling requests to an
                  endpoint but receive webhook payloads at a webhook URL, making
                  this trigger type a near-instant trigger.
                </p>
              </Fragment>
            ),
            links: [
              {
                href: `${BASE_DOC_URL_VB}/build/trigger#2-rest-hook-trigger`,
                title: 'REST Hook trigger',
              },
              {
                href: `${BASE_DOC_URL_VB}/build/hook-trigger`,
                title: 'Add a REST Hook trigger',
              },
            ],
          },
          contact: getDefaultFields(isLoggedIn),
        }),
      },
      {
        // Only rendered if no app selected or selected app is Visual Builder
        value: `${newOrExistingApp}-code-mode`,
        label: 'Using code mode',
        zendeskCustomFieldValue: `pcf-${newOrExistingApp}-building-triggers-code-mode`,
        shouldRender: context =>
          !context.selectedApp ||
          (context.selectedApp && !context.selectedApp.isCLIIntegration),
        child: help({
          docs: {
            title: 'Using Code Mode',
            body: () => (
              <Fragment>
                <p>
                  <Link
                    href={`${BASE_DOC_URL_VB}/build/code-mode`}
                    target="_blank"
                  >
                    Code Mode
                  </Link>{' '}
                  is an advanced Zapier feature in the Platform UI that allows
                  you to add further customization to your API calls.
                </p>
                <p>
                  By default, when setting up authentcation, triggers, or
                  actions, Form Mode is selected. You can then switch to Code
                  Mode to access Zapier's ‘z’ object library to customize your
                  API call, parse and transform your API response, and improve
                  error handling among other things.
                </p>
              </Fragment>
            ),
            links: [
              {
                href: `${BASE_DOC_URL_VB}/build/code-mode`,
                title: 'Use Code Mode to refine your API call',
              },
            ],
          },
          contact: getDefaultFields(isLoggedIn),
        }),
      },

      {
        // Only rendered if no app selected or selected app is CLI
        value: `${newOrExistingApp}-dynamic-fields-cli`,
        label: 'Dynamic and custom fields',
        zendeskCustomFieldValue: `pcf-${newOrExistingApp}-building-triggers-custom-fields`,
        shouldRender: context =>
          !context.selectedApp ||
          (context.selectedApp && context.selectedApp.isCLIIntegration),
        child: help({
          docs: {
            title: 'Building Dynamic and Custom Fields',
            body: () => (
              <Fragment>
                <p>
                  Dynamic fields are fields that are generated from an API call.
                  They can be dependent on, or independent off the data entered
                  in another input field and they are available in
                  <code>{` {{bundle.inputData}}`}</code>.
                </p>
                <p>
                  Unlike other field-types, dynamic fields are built by writing
                  JavaScript code to make an API call and fetch the fields
                  information from your app’s API. Your code should return an
                  array of objects, where each object represents a field
                  according to Zapier’s{' '}
                  <Link
                    href={`https://github.com/zapier/zapier-platform/blob/main/packages/schema/docs/build/schema.md#fieldschema`}
                    target="_blank"
                  >
                    field schema
                  </Link>
                  , so they display correctly in the Zap editor. These fields
                  then become available in <code>{`{{bundle.inputData}}`}</code>
                  .
                </p>
              </Fragment>
            ),
            links: [
              {
                href: `${BASE_DOC_URL_VB}/build/dynamic-field`,
                title:
                  'Send or receive dynamic user-defined fields through your API',
              },
            ],
          },
          contact: getDefaultFields(isLoggedIn),
        }),
      },
    ],
  }),
});

export const createSpecificError = (
  isLoggedIn: boolean,
  existingAppOrNewApp: string
) => ({
  value: 'error',
  label: 'Specific error when testing',
  zendeskCustomFieldValue: `pcf-${existingAppOrNewApp}-building-error`,
  child: radio({
    id: 'testing',
    label: 'What kind of error are you getting?',
    zendeskCustomFieldId:
      existingAppOrNewApp === 'new' ? 360044624891 : 360044627151,
    items: [
      {
        value: `${existingAppOrNewApp}-validation`,
        label: 'Resolving validation warnings/tasks',
        zendeskCustomFieldValue: `pcf-${existingAppOrNewApp}-building-error-validation`,
        child: help({
          docs: {
            title: 'Resolving validation warnings/tasks',
            body: () => (
              <Fragment>
                <p>
                  When we perform validation checks on your integration, we
                  check the integration for any structural issues and to ensure
                  that the integration gives our mutual users a seamless
                  experience and also satisfies some of our requirements.
                </p>
                <p>
                  The various validation checks we perform are listed{' '}
                  <Link
                    href={`${BASE_DOC_URL_VB}/publish/integration-checks-reference`}
                    target="_blank"
                  >
                    here
                  </Link>
                  .
                </p>
                <p>
                  Warnings are non-blocking and you can still publish your
                  integration if there are warnings. Errors and Publishing Tasks
                  are blocking, so an integration cannot be published unless
                  Errors and Publishing tasks are resolved.
                </p>
              </Fragment>
            ),
            links: [
              {
                href: `${BASE_DOC_URL_VB}/publish/integration-checks-reference`,
                title: 'Integration Checks Reference',
              },
            ],
          },
          contact: createDefaultContact({
            loggedIn: isLoggedIn,
            media: {
              label: `Please upload a screenshot of the warning or error you're having.`,
            },
          }),
        }),
      },
      {
        value: `${existingAppOrNewApp}-commands`,
        label: 'Error with Zapier commands',
        zendeskCustomFieldValue: `pcf-${existingAppOrNewApp}-building-error-commands`,
        // Only rendered if no app selected or selected app is CLI
        shouldRender: context =>
          !context.selectedApp ||
          (context.selectedApp && context.selectedApp.isCLIIntegration),
        child: help({
          contact: createDefaultContact({
            loggedIn: isLoggedIn,
            media: {
              label: `Please upload a screenshot of the warning or error you're having.`,
            },
          }),
        }),
      },
      {
        value: `${existingAppOrNewApp}-http`,
        label: 'HTTP request errors',
        zendeskCustomFieldValue: `pcf-${existingAppOrNewApp}-building-error-http`,
        // Only rendered if no app selected or selected app is CLI
        shouldRender: context =>
          !context.selectedApp ||
          (context.selectedApp && context.selectedApp.isCLIIntegration),
        child: help({
          contact: createDefaultContact({
            loggedIn: isLoggedIn,
            media: {
              label: `Please upload a screenshot of the warning or error you're having.`,
            },
          }),
        }),
      },
      {
        value: `${existingAppOrNewApp}-testing`,
        label: 'Error testing request',
        zendeskCustomFieldValue: `pcf-${existingAppOrNewApp}-building-error-testing`,
        // Only rendered if no app selected or selected app is Visual Builder
        shouldRender: context =>
          !context.selectedApp ||
          (context.selectedApp && !context.selectedApp.isCLIIntegration),
        child: help({
          contact: createDefaultContact({
            loggedIn: isLoggedIn,
            media: {
              label: `Please upload a screenshot of the warning or error you're having.`,
            },
          }),
        }),
      },
      {
        value: `${existingAppOrNewApp}-message`,
        label: 'Error message when using Zap',
        zendeskCustomFieldValue: `pcf-${existingAppOrNewApp}-building-error-using`,
        child: help({
          contact: createDefaultContact({
            loggedIn: isLoggedIn,
            media: {
              label: `Please upload a screenshot of the warning or error you're having.`,
            },
          }),
        }),
      },
      {
        value: `${existingAppOrNewApp}-version-control`,
        label: 'Version control',
        zendeskCustomFieldValue: `pcf-${existingAppOrNewApp}-building-error-version-control`,
        child: help({
          docs: {
            title: 'Version Control',
            body: () => (
              <Fragment>
                <p>
                  Versions allow you to create multiple iterations of your
                  integration to experiment with and implement new features
                  without affecting existing users. Each integration can have
                  many versions, but only one version can have a public status
                  at a one time.
                </p>
                <p>
                  When a version is ready to be used by the public, you can then{' '}
                  <Link
                    href={`${BASE_DOC_URL_VB}/manage/promote`}
                    target="_blank"
                  >
                    promote
                  </Link>{' '}
                  the version to be the default and public version and{' '}
                  <Link
                    href={`${BASE_DOC_URL_VB}/manage/migrate`}
                    target="_blank"
                  >
                    migrate
                  </Link>{' '}
                  users from older versions to the newly promoted version if
                  there are no breaking changes.
                </p>
                <p>
                  You can also choose to{' '}
                  <Link
                    href={`${BASE_DOC_URL_VB}/manage/deprecate`}
                    target="_blank"
                  >
                    deprecate or delete a non-public version that would no
                    longer be supported
                  </Link>{' '}
                </p>
              </Fragment>
            ),
            links: [
              {
                href: `${BASE_DOC_URL_VB}/manage/versions`,
                title: 'Manage versions in Platform UI',
              },
              {
                href: `${BASE_DOC_URL_VB}/reference/cli-docs#deploying-an-integration-version`,
                title: 'Manage versions in Platform CLI',
              },
            ],
          },
          contact: createDefaultContact({
            loggedIn: isLoggedIn,
            media: {
              label: `Please upload a screenshot of the warning or error you're having.`,
            },
          }),
        }),
      },
      {
        value: `${existingAppOrNewApp}-other`,
        label: 'Other',
        zendeskCustomFieldValue: `pcf-${existingAppOrNewApp}-building-error-other`,
        child: help({
          contact: createDefaultContact({
            loggedIn: isLoggedIn,
            media: {
              label: `Please upload a screenshot of the warning or error you're having.`,
            },
          }),
        }),
      },
    ],
  }),
});

export const createUsingResources = (
  isLoggedIn: boolean,
  existingAppOrNewApp: string
) => ({
  value: `${existingAppOrNewApp}-resources`,
  label: 'Using resources in a CLI app', // TODO: possibly add CLI icon
  zendeskCustomFieldValue: `pcf-${existingAppOrNewApp}-building-cli`,
  shouldRender: (context: { [prop: string]: any }) =>
    !context.selectedApp ||
    (context.selectedApp && context.selectedApp.isCLIIntegration),
  child: help({
    docs: {
      title: 'Using resources in a CLI app',
      body: () => (
        <Fragment>
          <p>
            A resource is a representation (as a JavaScript object) of one of
            the REST resources of your API. Say you have a /recipes endpoint for
            working with recipes; you can define a recipe resource in your app
            that will tell Zapier how to do create, read, and search operations
            on that resource.
          </p>
          <p>
            Check out{' '}
            <Link
              href="https://github.com/zapier/zapier-platform/tree/master/example-apps/resource"
              target="_blank"
            >
              this example app
            </Link>{' '}
            for a working example of using resources.{' '}
          </p>
        </Fragment>
      ),
      links: [
        {
          href: `${BASE_DOC_URL_VB}/reference/cli-docs#resources`,
          title: 'Using Resources - Documentation',
        },
      ],
    },
    contact: [
      ...getUserFields(isLoggedIn),
      {
        ...messageField,
        details: `Please let us know what questions you have about using Resources.`,
      },
    ],
  }),
});
